import React from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import useSeo from "../hooks/useSeo";
import { Check, Building2, Mail, Phone, MapPin, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOLUME_OPTIONS = [
  { v: "<10", label: "Under 10/md" },
  { v: "10-30", label: "10-30/md" },
  { v: "30-75", label: "30-75/md" },
  { v: "75+", label: "75+/md" },
];

const INDUSTRY_OPTIONS = [
  { v: "webshop", label: "Webshop / e-commerce" },
  { v: "advokat", label: "Advokat / juridisk" },
  { v: "klinik", label: "Klinik / tandlæge" },
  { v: "restaurant", label: "Restaurant / catering" },
  { v: "haandvaerker", label: "Håndværker / B2B-service" },
  { v: "other", label: "Andet" },
];

export default function BusinessSignupPage() {
  useSeo({
    title: "Erhvervskonto — 30 dages prøveperiode · EMA Kurer",
    description:
      "Aktivér din B2B-kurérkonto på 60 sekunder. 30 dages prøveperiode uden binding, månedsfaktura via Billy.dk, dedikeret kontaktperson. Fuld service fra dag ét.",
    path: "/erhverv/tilmeld",
  });

  const navigate = useNavigate();
  const [form, setForm] = React.useState({
    company_name: "",
    cvr: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    billing_address: "",
    billing_zip: "",
    billing_city: "",
    industry: "",
    expected_volume: "",
    tier_interest: "business",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState(null);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/business/self-signup`, form);
      setSuccess(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Noget gik galt — prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-brand-bg min-h-screen py-16" data-testid="signup-success">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-brand-dark">
            Velkommen til EMA Kurer Erhverv 🎉
          </h1>
          <p className="mt-4 text-brand-text2 leading-relaxed">
            Din 30-dages prøveperiode er aktiveret — uden binding. Vi har sendt en velkomst-email til <strong className="text-brand-dark">{form.contact_email}</strong> med dine næste skridt.
          </p>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-left">
            <div className="text-xs uppercase tracking-widest font-bold text-brand-red mb-2">Prøveperiode aktiv</div>
            <div className="text-2xl font-display font-bold text-brand-dark">
              Gælder til {success.trial_ends_at?.slice(0, 10)}
            </div>
            <p className="text-sm text-brand-text2 mt-2">
              I prøveperioden får du fuld service til erhvervspriser uden binding — opsig når du vil. Vores team kontakter dig inden for 2 hverdage.
            </p>
          </div>
          <button
            onClick={() => navigate("/book?type=business")}
            className="mt-6 inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 rounded-lg font-bold hover:bg-brand-red/90"
            data-testid="signup-cta-book"
          >
            Book din første levering →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-bg min-h-screen" data-testid="business-signup-page">
      {/* Hero */}
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-brand-red/20 text-brand-red px-3 py-1.5 rounded-full mb-4">
            <Sparkles size={14} /> 30 dages prøveperiode · uden binding
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Aktivér din erhvervskonto på 60 sekunder
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">
            Få straks adgang til erhvervspriser, månedsfaktura via Billy.dk og dedikeret kontaktperson.
            Vi kontakter dig inden for 2 hverdage til personlig onboarding.
          </p>
        </div>
      </section>

      {/* Benefits + Form */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Benefits sidebar */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-display text-lg font-bold text-brand-dark mb-3">Hvad får du?</h3>
              <ul className="space-y-2.5 text-sm text-brand-text2">
                {[
                  "Erhvervspriser fra dag ét",
                  "Månedsfaktura via Billy.dk eller e-conomic",
                  "Netto 14 dages betaling",
                  "Dedikeret kontaktperson",
                  "API + CSV bulk-upload",
                  "Prioriteret afhentning",
                  "Ingen binding — opsig når du vil",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="text-xs uppercase font-bold text-amber-800 mb-1">Volumen over 75/md?</div>
              <p className="text-sm text-amber-900">
                Kontakt vores salgsteam for en skræddersyet aftale fra 8.999 kr/md.
              </p>
              <Link to="/kontakt?subject=Premium" className="mt-2 inline-block text-sm font-bold text-amber-900 underline">
                Tal med salg →
              </Link>
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5" data-testid="signup-form">
            {error && (
              <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-800 text-sm" data-testid="signup-error">
                {error}
              </div>
            )}

            <div>
              <h2 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
                <Building2 size={18} /> Virksomhed
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="Firmanavn *"
                  value={form.company_name}
                  onChange={u("company_name")}
                  required
                  data-testid="signup-company-name"
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="CVR (8 cifre) *"
                  maxLength={8}
                  pattern="[0-9]{8}"
                  value={form.cvr}
                  onChange={u("cvr")}
                  required
                  data-testid="signup-cvr"
                />
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
                <Mail size={18} /> Kontaktperson
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm sm:col-span-2"
                  placeholder="Fuldt navn *"
                  value={form.contact_name}
                  onChange={u("contact_name")}
                  required
                  data-testid="signup-contact-name"
                />
                <input
                  type="email"
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="Email *"
                  value={form.contact_email}
                  onChange={u("contact_email")}
                  required
                  data-testid="signup-contact-email"
                />
                <input
                  type="tel"
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="Telefon *"
                  value={form.contact_phone}
                  onChange={u("contact_phone")}
                  required
                  data-testid="signup-contact-phone"
                />
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
                <MapPin size={18} /> Faktureringsadresse (valgfri)
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm sm:col-span-3"
                  placeholder="Adresse"
                  value={form.billing_address}
                  onChange={u("billing_address")}
                  data-testid="signup-billing-address"
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="Postnr"
                  value={form.billing_zip}
                  onChange={u("billing_zip")}
                  data-testid="signup-billing-zip"
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm sm:col-span-2"
                  placeholder="By"
                  value={form.billing_city}
                  onChange={u("billing_city")}
                  data-testid="signup-billing-city"
                />
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-bold text-brand-dark">Volumen & branche</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <select
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white"
                  value={form.industry}
                  onChange={u("industry")}
                  data-testid="signup-industry"
                >
                  <option value="">Vælg branche</option>
                  {INDUSTRY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                <select
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white"
                  value={form.expected_volume}
                  onChange={u("expected_volume")}
                  data-testid="signup-volume"
                >
                  <option value="">Forventet volumen / md</option>
                  {VOLUME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-brand-red text-white px-6 py-3.5 font-bold hover:bg-brand-red/90 disabled:opacity-50 transition-colors"
              data-testid="signup-submit"
            >
              {submitting ? "Aktiverer..." : "Aktivér 30-dages prøveperiode"}
            </button>
            <p className="text-xs text-brand-text2 text-center">
              Ved tilmelding accepterer du vores{" "}
              <Link to="/handelsbetingelser" className="underline hover:text-brand-red">handelsbetingelser</Link>.
              Ingen binding · stop når du vil.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
